/**
 * DocumentChecklist - Document Requirements Inline Display
 * =========================================================
 * 
 * Displays the document requirements checklist at the top of the case detail view.
 * Shows what documents are required, uploaded, and missing based on case type.
 * 
 * This is a HARD GATE - cases cannot proceed to Accountant queue without all required docs.
 * 
 * Props:
 *   caseId: string - The draft/report ID
 *   caseType?: string - Override case type (optional, fetched if not provided)
 *   entityType?: string - Override entity type (optional, fetched if not provided)
 *   onCaseTypeChange?: (caseType: string) => void - Callback when case type changes
 *   compact?: boolean - Render in compact mode for sidebar
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../utils/api';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader2,
  Settings2,
  Ban,
  RefreshCw,
  Calendar
} from 'lucide-react';

// Types
interface CoverageInfo {
  data_source: 'canonical_data' | 'financial_data' | 'filenames' | 'none';
  months_uploaded: number;
  months_required: number | null;
  months_suggested?: number | null;
  years_covered: number[];
  coverage_range: string | null;
  missing_count: number;
  missing_months: Array<{year: number; month: number; display: string}>;
  missing_summary: string | null;
  suggested_missing_count?: number;
  suggested_missing?: Array<{year: number; month: number; display: string}>;
  suggested_summary?: string | null;
  is_complete: boolean | null;
  discrepancies?: Array<{filename: string; expected: string; issue: string}>;
}

interface DocumentRequirement {
  doc_type: string;
  display_name: string;
  description: string;
  category: string;
  status: 'required' | 'optional' | 'n/a';
  conditional: boolean;
  condition: string | null;
  uploaded: boolean;
  upload_status: string;
  file_ids: string[];
  file_names: string[];
  file_count: number;
  coverage?: CoverageInfo | null;
}

interface CompletionStats {
  required_count: number;
  uploaded_count: number;
  percentage: number;
  ready_for_accountant: boolean;
}

interface Blocker {
  type: string;
  doc_type?: string;
  display_name?: string;
  message: string;
}

interface DocumentRequirementsResponse {
  case_id: string;
  case_type: string;
  case_type_name: string;
  entity_type: string;
  claim_source: string;
  has_pl: boolean;
  requirements: DocumentRequirement[];
  completion: CompletionStats;
  blockers: Blocker[];
  warnings: Blocker[];
  assessed_at: string;
}

interface CaseTypeOption {
  code: string;
  name: string;
}

interface DocumentChecklistProps {
  caseId: string;
  caseType?: string;
  entityType?: string;
  onCaseTypeChange?: (caseType: string) => void;
  onReadinessChange?: (ready: boolean) => void;
  onCompletionChange?: (stats: CompletionStats) => void;
  compact?: boolean;
  className?: string;
}

// Case type display helper
const CASE_TYPE_ICONS: Record<string, string> = {
  'BI': '🏢',
  'FC': '🔒',
  'LP': '📉',
  'FRAUD': '🔍',
  'VAL': '💰',
  'PD': '🤝',
  'CONTRACT': '📜',
  'TRADE': '🔐',
  'EMP_WT': '👨‍💼',
  'EMP_WH': '⏰',
  'EMP_DISC': '⚖️',
  'FAM_DIV': '👪',
  'FAM_CS': '👶',
  'PI_WAGE': '🏥',
  'PI_EARN': '📊',
  'CUSTOM': '⚙️',
};

export default function DocumentChecklist({
  caseId,
  caseType: propCaseType,
  entityType: propEntityType,
  onCaseTypeChange,
  onReadinessChange,
  onCompletionChange,
  compact = false,
  className = ''
}: DocumentChecklistProps) {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<DocumentRequirementsResponse | null>(null);
  const [caseTypes, setCaseTypes] = useState<CaseTypeOption[]>([]);
  const [expanded, setExpanded] = useState(!compact);
  const [showCaseTypeSelector, setShowCaseTypeSelector] = useState(false);
  const [updatingCaseType, setUpdatingCaseType] = useState(false);

  // Request-sequence guard: ignore stale in-flight responses when caseId
  // changes rapidly (tab switches, fast navigation).
  const requestSeq = useRef(0);

  // Fetch case types once
  useEffect(() => {
    const fetchCaseTypes = async () => {
      try {
        const response = await api.get('/api/v1/case-types', { showErrorToast: false });
        setCaseTypes(response.case_types || []);
      } catch (err) {
        console.warn('Failed to fetch case types:', err);
      }
    };
    fetchCaseTypes();
  }, []);

  // Hold callbacks in refs so parent-render churn doesn't re-trigger effects.
  const onReadinessChangeRef = useRef(onReadinessChange);
  useEffect(() => {
    onReadinessChangeRef.current = onReadinessChange;
  }, [onReadinessChange]);

  const onCompletionChangeRef = useRef(onCompletionChange);
  useEffect(() => {
    onCompletionChangeRef.current = onCompletionChange;
  }, [onCompletionChange]);

  // Fetch document requirements.  Dep only on caseId — callbacks are
  // accessed via ref so identity churn from the parent does NOT cascade.
  const fetchRequirements = useCallback(async () => {
    if (!caseId) return;

    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(
        `/api/v1/reports/${caseId}/document-requirements`,
        { showErrorToast: false, timeoutMs: 30000 }
      );
      if (seq !== requestSeq.current) return; // stale
      setRequirements(response);

      // Notify parent of readiness change via ref (latest closure value)
      const readinessCb = onReadinessChangeRef.current;
      if (readinessCb) {
        readinessCb(response.completion?.ready_for_accountant || false);
      }
      const completionCb = onCompletionChangeRef.current;
      if (completionCb && response.completion) {
        completionCb(response.completion);
      }
    } catch (err: any) {
      if (seq !== requestSeq.current) return; // stale
      if (err.status === 404) {
        setError('Case not found');
      } else {
        setError('Failed to load document requirements');
      }
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
      }
    }
  }, [caseId]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  // Empty caseId guard — render unavailable card instead of firing a
  // guaranteed-404 (or worse, a request with an empty path segment).
  // MUST be after all hooks (Rules of Hooks).
  if (!caseId) {
    return (
      <div
        className={`rounded-lg border p-4 ${className}`}
        style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <p className="cs-body" style={{ color: 'var(--color-text-muted)' }}>Document checklist unavailable</p>
      </div>
    );
  }

  // Update case type
  const handleCaseTypeChange = async (newCaseType: string) => {
    setUpdatingCaseType(true);
    try {
      await api.post(`/api/v1/reports/${caseId}/case-type`, {
        case_type: newCaseType
      }, { showErrorToast: false });
      
      // Refresh requirements
      await fetchRequirements();
      
      // Notify parent
      if (onCaseTypeChange) {
        onCaseTypeChange(newCaseType);
      }
      
      setShowCaseTypeSelector(false);
    } catch (err) {
      console.error('Failed to update case type:', err);
    } finally {
      setUpdatingCaseType(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div
        className={`rounded-lg border p-4 ${className}`}
        style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <div className="flex items-center gap-2 cs-meta">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          <span>Loading document requirements...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div
        className={`rounded-lg border p-4 ${className}`}
        style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
      >
        <div className="flex items-center gap-2 cs-body" style={{ color: 'var(--color-needs-human-text)' }}>
          <XCircle className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // Absent OR degraded payload → explicit on-brand unavailable card (never a
  // silent null). Page-sweep (2026-07-28): the case-status page (/case/:id)
  // blanked to the global ErrorBoundary because a partial payload left
  // requirements.requirements undefined and the raw .filter threw. Guard the
  // WHOLE essential shape (both the requirements array AND completion, which is
  // read unguarded as completion.percentage below) so a degraded payload
  // renders the unavailable card instead of crashing one field deeper.
  if (!requirements || !Array.isArray(requirements.requirements) || !requirements.completion) {
    return (
      <div
        className={`rounded-lg border p-4 ${className}`}
        style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <p className="cs-body" style={{ color: 'var(--color-text-muted)' }}>Document checklist unavailable</p>
      </div>
    );
  }

  // Default the sibling arrays: a degraded payload can present requirements +
  // completion (passing the gate above) yet omit blockers/warnings, and
  // blockers.length/.slice are read unguarded at the blockers notice below —
  // that crashed /case/:id (independent review of the 2026-07-28 hand-fix
  // caught this exact "guard moved the crash one field deeper" miss).
  const { completion, blockers = [], warnings = [] } = requirements;
  const reqList = requirements.requirements;
  const requiredDocs = reqList.filter(r => r.status === 'required');
  const optionalDocs = reqList.filter(r => r.status === 'optional');
  const naiveDocs = reqList.filter(r => r.status === 'n/a');

  // Compact mode render
  if (compact) {
    return (
      <div
        className={`rounded-lg border ${className}`}
        style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-3 transition-colors"
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
            <span className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>Document Checklist</span>
            <span
              className="cs-chip px-2 py-0.5 rounded-full"
              style={
                completion.ready_for_accountant
                  ? { background: 'transparent', color: 'var(--color-verified)' }
                  : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
              }
            >
              {completion.uploaded_count}/{completion.required_count}
            </span>
          </div>
          {expanded
            ? <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
            : <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />}
        </button>

        {/* Expanded content */}
        {expanded && (
          <div className="p-3 pt-0 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
            {renderDocumentList()}
          </div>
        )}
      </div>
    );
  }

  // Full mode render - inline at top of case detail
  function renderDocumentList() {
    return (
      <div className="space-y-2 mt-2">
        {requiredDocs.map(doc => (
          <div key={doc.doc_type} className="flex items-start gap-2 cs-body">
            {doc.uploaded ? (
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-verified)' }} />
            ) : (
              <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span
                  style={doc.uploaded
                    ? { color: 'var(--color-text-secondary)' }
                    : { color: 'var(--color-needs-human-text)', fontWeight: 500 }}
                >
                  {doc.display_name}
                </span>
                {doc.conditional && doc.condition && (
                  <span className="cs-meta" style={{ color: 'var(--color-text-dim)' }} title={doc.condition}>*</span>
                )}
              </div>
              {doc.uploaded && doc.file_count > 0 && (
                <span className="cs-meta">
                  {doc.file_count} file{doc.file_count > 1 ? 's' : ''} uploaded
                </span>
              )}
            </div>
          </div>
        ))}

        {/* Not Applicable items (collapsed) */}
        {naiveDocs.length > 0 && (
          <div className="cs-meta pt-1 border-t mt-2" style={{ color: 'var(--color-text-dim)', borderColor: 'var(--color-border-sub)' }}>
            <span className="flex items-center gap-1">
              <Ban className="w-3 h-3" />
              {naiveDocs.length} not required for this case
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border overflow-hidden ${className}`}
      style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
    >
      {/* Header with case type and completion status */}
      <div className="p-4 border-b" style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-sub)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl" title={requirements.case_type_name}>
              {CASE_TYPE_ICONS[requirements.case_type] || '📋'}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="cs-h3">
                  Document Checklist
                </h3>
                <span
                  className="cs-chip px-2 py-0.5 rounded-full"
                  style={
                    completion.ready_for_accountant
                      ? { background: 'transparent', color: 'var(--color-verified)' }
                      : { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                  }
                >
                  {completion.uploaded_count} of {completion.required_count} ✓
                </span>
              </div>
              <div className="flex items-center gap-2 cs-meta">
                <span>Case Type: {requirements.case_type_name}</span>
                {requirements.entity_type && requirements.entity_type !== 'unknown' && (
                  <>
                    <span>•</span>
                    <span className="capitalize">{requirements.entity_type.replace('_', ' ')}</span>
                  </>
                )}
                {requirements.claim_source && requirements.claim_source !== 'unknown' && (
                  <>
                    <span>•</span>
                    <span
                      className="cs-chip px-1.5 py-0.5 rounded"
                      style={
                        requirements.claim_source === 'first_party'
                          ? { background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                          : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
                      }
                    >
                      {requirements.claim_source === 'first_party' ? '1st Party' : '3rd Party'}
                    </span>
                  </>
                )}
                <button
                  onClick={() => setShowCaseTypeSelector(!showCaseTypeSelector)}
                  className="ml-1 p-1 rounded transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  title="Change case type"
                >
                  <Settings2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={fetchRequirements}
            className="p-2 rounded-lg transition-colors"
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          </button>
        </div>
        
        {/* Case Type Selector (hidden by default) */}
        {showCaseTypeSelector && (
          <div
            className="mt-3 p-3 rounded-lg border shadow-sm"
            style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
          >
            <label className="block cs-label mb-2">Change Case Type</label>
            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
              {caseTypes.map(ct => (
                <button
                  key={ct.code}
                  onClick={() => handleCaseTypeChange(ct.code)}
                  disabled={updatingCaseType}
                  className="text-left px-2 py-1.5 cs-meta rounded border transition-colors"
                  style={
                    requirements.case_type === ct.code
                      ? { background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }
                      : { borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }
                  }
                  onMouseEnter={(e) => { if (requirements.case_type !== ct.code) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                  onMouseLeave={(e) => { if (requirements.case_type !== ct.code) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span className="mr-1">{CASE_TYPE_ICONS[ct.code] || '📋'}</span>
                  {ct.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="px-4 py-2" style={{ background: 'var(--color-bg-base)' }}>
        <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-bg-elevated)' }}>
          <div
            className="h-full transition-all duration-300"
            style={{
              width: `${completion.percentage}%`,
              background: completion.ready_for_accountant ? 'var(--color-verified)' : 'var(--color-brand)',
            }}
          />
        </div>
        <div className="flex justify-between cs-meta mt-1">
          <span>{completion.percentage}% complete</span>
          <span>
            {completion.ready_for_accountant
              ? '✓ Ready for Accountant'
              : `${completion.required_count - completion.uploaded_count} required documents missing`
            }
          </span>
        </div>
      </div>
      
      {/* Document List */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Required Documents */}
          {requiredDocs.map(doc => (
            <div
              key={doc.doc_type}
              className="flex items-start gap-3 p-3 rounded-lg border"
              style={
                doc.uploaded
                  ? { background: 'transparent', borderColor: 'var(--color-verified)' }
                  : { background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }
              }
            >
              {doc.uploaded ? (
                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-verified)' }} />
              ) : (
                <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>
                  {doc.display_name}
                </div>
                {doc.uploaded ? (
                  <div className="cs-meta mt-0.5" style={{ color: 'var(--color-verified)' }}>
                    {doc.file_count} file{doc.file_count > 1 ? 's' : ''} uploaded
                    {/* Show coverage info for time-series documents */}
                    {doc.coverage && doc.coverage.coverage_range && (
                      <span className="flex items-center gap-1" style={{ color: 'var(--color-verified)', fontWeight: 500 }}>
                        <Calendar className="w-3.5 h-3.5 flex-shrink-0" /> {doc.coverage.coverage_range} ({doc.coverage.months_uploaded} months)
                        {doc.coverage.data_source === 'canonical_data' && (
                          <span className="ml-1 cs-meta" style={{ color: 'var(--color-text-dim)' }} title="Verified from parsed data">✓ verified</span>
                        )}
                      </span>
                    )}
                    {/* Show missing months if any (required/loss period gaps) */}
                    {doc.coverage && doc.coverage.missing_count > 0 && (
                      <span className="flex items-start gap-1 mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Missing: {doc.coverage.missing_months && doc.coverage.missing_months.length > 0
                          ? doc.coverage.missing_months.map(m => m.display).join(', ')
                          : doc.coverage.missing_summary}
                      </span>
                    )}
                    {/* Show suggested pre-loss months as soft recommendation */}
                    {doc.coverage && doc.coverage.suggested_missing_count > 0 && doc.coverage.missing_count === 0 && (
                      <span className="block mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                        Suggested: {doc.coverage.suggested_summary} (pre-loss baseline)
                      </span>
                    )}
                    {/* Show discrepancies if found */}
                    {doc.coverage && doc.coverage.discrepancies && doc.coverage.discrepancies.length > 0 && (
                      <span className="block mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                        🚨 Data mismatch: {doc.coverage.discrepancies[0].issue}
                      </span>
                    )}
                    {doc.file_names.length > 0 && !doc.coverage?.coverage_range && (
                      <span className="block truncate" style={{ color: 'var(--color-text-muted)' }} title={doc.file_names.join(', ')}>
                        {doc.file_names[0]}{doc.file_names.length > 1 ? ` +${doc.file_names.length - 1} more` : ''}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="cs-meta mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>
                    {/* Show what's specifically missing */}
                    {doc.coverage && doc.coverage.months_required ? (
                      <>
                        <span style={{ fontWeight: 500 }}>Missing {doc.coverage.months_required} months of data</span>
                        {doc.coverage.missing_months && doc.coverage.missing_months.length > 0 && (
                          <span className="block mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                            Need: {doc.coverage.missing_months.slice(0, 3).map(m => m.display).join(', ')}
                            {doc.coverage.missing_months.length > 3 && ` +${doc.coverage.missing_months.length - 3} more`}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        Missing - required for this case type
                        {doc.conditional && doc.condition && (
                          <span className="block" style={{ color: 'var(--color-text-muted)' }}>{doc.condition}</span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Optional Documents (collapsed) */}
        {optionalDocs.length > 0 && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <details className="group">
              <summary className="flex items-center gap-2 cursor-pointer cs-body" style={{ color: 'var(--color-text-secondary)' }}>
                <AlertTriangle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                <span>Optional Documents ({optionalDocs.length})</span>
                <ChevronDown className="w-4 h-4 ml-auto group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                {optionalDocs.map(doc => (
                  <div key={doc.doc_type} className="flex items-center gap-2 cs-body p-2 rounded" style={{ background: 'var(--color-bg-elevated)' }}>
                    {doc.uploaded ? (
                      <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                    ) : (
                      <AlertTriangle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                    )}
                    <span style={{ color: 'var(--color-text-secondary)' }}>{doc.display_name}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        {/* Not Applicable Documents (collapsed) */}
        {naiveDocs.length > 0 && (
          <div className="mt-3">
            <details className="group">
              <summary className="flex items-center gap-2 cursor-pointer cs-meta" style={{ color: 'var(--color-text-dim)' }}>
                <Ban className="w-3 h-3" />
                <span>Not applicable for this case ({naiveDocs.length})</span>
                <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-2 flex flex-wrap gap-1">
                {naiveDocs.map(doc => (
                  <span key={doc.doc_type} className="cs-meta px-2 py-0.5 rounded" style={{ color: 'var(--color-text-dim)', background: 'var(--color-bg-elevated)' }}>
                    {doc.display_name}
                    {doc.condition && <span className="ml-1" title={doc.condition}>*</span>}
                  </span>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Blocker Banner - shows when not ready */}
      {!completion.ready_for_accountant && blockers.length > 0 && (
        <div className="border-t p-4" style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
          <div className="flex items-start gap-2">
            <Ban className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
            <div>
              <p className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>Cannot proceed to Accountant</p>
              <ul className="mt-1 cs-body list-disc list-inside" style={{ color: 'var(--color-needs-human-text)' }}>
                {blockers.slice(0, 3).map((b, i) => (
                  <li key={i}>{b.message}</li>
                ))}
                {blockers.length > 3 && (
                  <li>...and {blockers.length - 3} more</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Ready Banner */}
      {completion.ready_for_accountant && (
        <div className="border-t p-4" style={{ background: 'transparent', borderColor: 'var(--color-verified)' }}>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
            <p className="cs-body" style={{ color: 'var(--color-verified)', fontWeight: 500 }}>
              All required documents collected - ready for Accountant review
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
